import { useMemo } from 'react';
import { TaskCompletion } from 'graphql-common';
import buildTaskCompletionDetailsRows from '@lib/components/TaskCompletions/utils/buildTaskCompletionDetailsRows';
import useTaskCompletionQueryHook from '@lib/utils/fetch/hooks/useTaskCompletionQueryHook';
import alignCompareRows from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/utils/alignCompareRows';
import {
  CompareCompletionColumn,
  CompareCompletionRow,
} from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/utils/types';
import { COMPARE_PAGE_SIZE } from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/utils/constants';

export default function useCompareCompletionsData(
  ids: string[],
  isViewLocked?: boolean,
) {
  // Fixed number of hook calls (max page size) regardless of how many slots
  // are actually filled, so the hook order never changes across renders.
  const slot0 = useTaskCompletionQueryHook({
    id: ids[0] || '',
    isViewLocked,
    skip: !ids[0],
  });
  const slot1 = useTaskCompletionQueryHook({
    id: ids[1] || '',
    isViewLocked,
    skip: !ids[1],
  });
  const slot2 = useTaskCompletionQueryHook({
    id: ids[2] || '',
    isViewLocked,
    skip: !ids[2],
  });
  const slots = [slot0, slot1, slot2].slice(0, COMPARE_PAGE_SIZE);

  const columns = useMemo<CompareCompletionColumn[]>(
    () =>
      ids.map((id, index) => {
        const { data } = slots[index];
        return {
          id,
          completion: data as TaskCompletion | null | undefined,
          rows: buildTaskCompletionDetailsRows(
            data?.task?.formTemplateVersion?.questions,
            data?.answers,
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids, slot0.data, slot1.data, slot2.data],
  );

  const rows = useMemo<CompareCompletionRow[]>(
    () => alignCompareRows(columns),
    [columns],
  );

  const firstLoading = slots
    .slice(0, ids.length)
    .some((slot) => slot.firstLoading);
  const loading = slots.slice(0, ids.length).some((slot) => slot.loading);
  const error = slots.slice(0, ids.length).find((slot) => slot.error)?.error;

  return {
    columns,
    rows,
    firstLoading,
    loading,
    error,
  };
}
